import type { CSSProperties, ReactNode } from 'react';
import { DT, DS2 } from '../theme/designTokens';
import { useIsMobile } from '../hooks/useBreakpoint';
import { PageContent } from '../components/ui/PageContent';
import { GradientText } from '../components/ui/GradientText';
import { DS2Button } from '../components/ui/DS2Button';
import { Footer } from '../components/shell/Footer';
import { getDominoAssetPath } from '../config/dominoAssets';
import { useTranslation, Trans } from '../i18n/react';

/**
 * RULES — public how-to-play reference for the implemented Latin Domino
 * rules. Deliberately PUBLIC (no RequireAuth): the marketing footer
 * links here for logged-out visitors, and the shared AppShell already adapts
 * its chrome to auth state (logged-out sidebar shows Sign Up / Log In, same
 * as Landing), so one route serves both audiences.
 *
 * Every rule stated here was verified against the backend engine and specs
 * (RoundFinalizerService, PlayTileService, PassTurnService, LegalMovesService,
 * CreateFirst/NextGameService, TurnTimeoutJob). Do NOT add rules the backend
 * does not implement — there is no capicúa/bonus scoring, no draw pile, no
 * spinner, and later rounds start by seat rotation (not winner-starts).
 */

// ── Tile diagrams — real game assets via the canonical resolver ─────────────
//
// Explicit width AND height (exact 2:1 source ratio) plus maxWidth:'none'
// sidestep Tailwind preflight's `img { max-width: 100%; height: auto }` cap
// entirely (see OrdinaryRoundEndOverlay.tsx for the full pathology).

function Tile({ a, b, size, flip }: { a: number; b: number; size: number; flip?: boolean }) {
  const { t } = useTranslation('rules');
  return (
    <img
      src={getDominoAssetPath(a, b)}
      alt={t('tileAlt', { a: Math.min(a, b), b: Math.max(a, b) })}
      style={{
        width:     size,
        height:    size / 2,
        maxWidth:  'none',
        display:   'block',
        flexShrink: 0,
        transform: flip ? 'scaleX(-1)' : undefined,
      }}
      draggable={false}
    />
  );
}

/** A double placed crosswise: the horizontal source art rotated 90° inside a
 *  wrapper reserving its post-rotation (half-width, full-height) box. */
function CrossTile({ v, size }: { v: number; size: number }) {
  const { t } = useTranslation('rules');
  return (
    <span
      style={{
        width:          size / 2,
        height:         size,
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        flexShrink:     0,
      }}
    >
      <img
        src={getDominoAssetPath(v, v)}
        alt={t('crossTileAlt', { n: v })}
        style={{ width: size, height: size / 2, maxWidth: 'none', display: 'block', transform: 'rotate(90deg)' }}
        draggable={false}
      />
    </span>
  );
}

function Diagram({ caption, children }: { caption: string; children: ReactNode }) {
  return (
    <figure style={{ margin: '18px 0 0' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 3, overflowX: 'auto', paddingBottom: 2 }}>
        {children}
      </div>
      <figcaption
        style={{
          marginTop:  10,
          fontSize:   13,
          lineHeight: 1.5,
          fontFamily: DS2.font.small,
          color:      '#CCCCCC',
        }}
      >
        {caption}
      </figcaption>
    </figure>
  );
}

// ── Section / typography atoms ──────────────────────────────────────────────

const BODY_TEXT: CSSProperties = {
  fontSize:   'clamp(0.9rem, 0.72vw, 16px)',
  lineHeight: 1.7,
  fontFamily: DS2.font.body,
  fontWeight: 300,
  color:      DS2.color.textBright,
  margin:     '10px 0 0',
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  const isMobile = useIsMobile();
  return (
    <section
      style={{
        background:   DS2.color.surface,
        border:       `1px solid ${DS2.color.cardBorder}`,
        borderRadius: DS2.radius.card,
        padding:      isMobile ? 20 : DS2.spacing.cardPadding,
        marginTop:    isMobile ? 16 : 21,
      }}
    >
      <h2
        style={{
          fontSize:      isMobile ? 18 : `clamp(1.15rem, ${DS2.type.cardTitle.pref}vw, ${DS2.type.cardTitle.max}px)`,
          lineHeight:    1.25,
          letterSpacing: '-0.02em',
          fontWeight:    700,
          fontFamily:    DS2.font.heading,
          color:         '#FFFFFF',
          margin:        0,
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Subsection heading — gold-yellow per the DS2 column-header treatment. */
function Sub({ children }: { children: ReactNode }) {
  return (
    <h3
      style={{
        fontSize:   15,
        lineHeight: 1.4,
        fontWeight: 500,
        fontFamily: DS2.font.body,
        color:      '#FFC844',
        margin:     '20px 0 0',
      }}
    >
      {children}
    </h3>
  );
}

// ── Screen ──────────────────────────────────────────────────────────────────

export function RulesScreen() {
  // 'navigation' rides along for the Play Online CTA — semantically the same
  // action label as the nav entry, so the copy stays single-sourced.
  const { t } = useTranslation(['rules', 'navigation']);
  const isMobile = useIsMobile();
  const tileSize = isMobile ? 58 : 76;

  return (
    // flex column fills the available viewport height so Footer's
    // marginTop:'auto' sticks flush to the bottom on short pages (see
    // AppShell.tsx's content-wrapper comment).
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

      <div style={{ paddingTop: isMobile ? 28 : 53, paddingBottom: isMobile ? 28 : 53 }}>
        <PageContent maxWidth={DS2.layout.contentMaxWidth}>
          <h1
            style={{
              fontSize:      isMobile ? 22 : `clamp(1.6rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
              lineHeight:    isMobile ? '26px' : DS2.type.sectionHeading.lineHeight,
              letterSpacing: '-0.02em',
              fontWeight:    700,
              fontFamily:    DS2.font.heading,
              color:         '#FFFFFF',
              margin:        0,
            }}
          >
            {/* The catalog owns which word carries the gradient, so es-419
                can move it despite the reversed word order. */}
            <Trans t={t} i18nKey="rules:heading" components={{ accent: <GradientText>{null}</GradientText> }} />
          </h1>
          <p style={{ ...BODY_TEXT, margin: isMobile ? '10px 0 0' : '14.7px 0 0', maxWidth: 760 }}>
            {t('rules:intro')}
          </p>

          {/* Content column — capped for comfortable reading line length */}
          <div style={{ maxWidth: 860 }}>

            <Section title={t('rules:basics.title')}>
              <p style={BODY_TEXT}>{t('rules:basics.intro')}</p>
              <Sub>{t('rules:basics.setup')}</Sub>
              <p style={BODY_TEXT}>{t('rules:basics.setupBody')}</p>
              <Sub>{t('rules:basics.starting')}</Sub>
              <p style={BODY_TEXT}>{t('rules:basics.startingBody')}</p>
              <Diagram caption={t('rules:basics.startingCaption')}>
                <Tile a={6} b={6} size={tileSize} />
              </Diagram>
            </Section>

            <Section title={t('rules:howToPlay.title')}>
              <Sub>{t('rules:howToPlay.matching')}</Sub>
              <p style={BODY_TEXT}>{t('rules:howToPlay.matchingBody')}</p>
              <Diagram caption={t('rules:howToPlay.matchingCaption')}>
                <Tile a={1} b={3} size={tileSize} />
                <Tile a={3} b={6} size={tileSize} />
                <Tile a={2} b={6} size={tileSize} flip />
              </Diagram>
              <Sub>{t('rules:howToPlay.doubles')}</Sub>
              <p style={BODY_TEXT}>{t('rules:howToPlay.doublesBody')}</p>
              <Diagram caption={t('rules:howToPlay.doublesCaption')}>
                <Tile a={4} b={6} size={tileSize} />
                <CrossTile v={6} size={tileSize} />
                <Tile a={2} b={6} size={tileSize} flip />
              </Diagram>
              <Sub>{t('rules:howToPlay.passing')}</Sub>
              <p style={BODY_TEXT}>{t('rules:howToPlay.passingBody')}</p>
            </Section>

            <Section title={t('rules:endingRound.title')}>
              <Sub>{t('rules:endingRound.domino')}</Sub>
              <p style={BODY_TEXT}>{t('rules:endingRound.dominoBody')}</p>
              <Sub>{t('rules:endingRound.blocked')}</Sub>
              <p style={BODY_TEXT}>{t('rules:endingRound.blockedBody')}</p>
              <Sub>{t('rules:endingRound.tie')}</Sub>
              <p style={BODY_TEXT}>{t('rules:endingRound.tieBody')}</p>
            </Section>

            <Section title={t('rules:scoring.title')}>
              <p style={BODY_TEXT}>{t('rules:scoring.intro')}</p>
              <Sub>{t('rules:scoring.example')}</Sub>
              <p style={BODY_TEXT}>{t('rules:scoring.exampleBody')}</p>
              <Diagram caption={t('rules:scoring.exampleCaption')}>
                <Tile a={5} b={6} size={tileSize} />
                <Tile a={4} b={4} size={tileSize} />
                <Tile a={1} b={2} size={tileSize} />
              </Diagram>
            </Section>

            <Section title={t('rules:winning.title')}>
              <p style={BODY_TEXT}>{t('rules:winning.body')}</p>
              <div style={{ marginTop: 22 }}>
                <DS2Button to="/play/online" variant="gold">{t('navigation:items.playOnline')}</DS2Button>
              </div>
            </Section>

          </div>
        </PageContent>
      </div>

      <Footer />
    </div>
  );
}
